import React, { useEffect, useState } from 'react';
import {
    X, Sparkles, Video, UtensilsCrossed, FileText, Image as ImageIcon,
    Mic, Tag, Clock, ArrowRight, Loader2, Crown,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = '/api';

/**
 * AIUsageStatsModal - shows concrete value the user got from their Premium
 * AI quota (videos transcribed, recipes extracted, hours saved, etc.).
 * Opened by tapping the quota progress bar on the dashboard. Includes a
 * CTA to open the upgrade modal so it doubles as a conversion surface.
 */
export default function AIUsageStatsModal({ open, onClose, onUpgrade, subscriptionInfo = {} }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetch(`${API}/subscriptions/ai-usage-stats`, { credentials: 'include' })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error('Could not load stats'))))
            .then(data => { if (!cancelled) setStats(data); })
            .catch(err => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open]);

    if (!open) return null;

    const tier = subscriptionInfo.tier || 'free';
    const tierLabel = tier === 'pro_unlimited' ? 'BrainSync Pro' : tier === 'pro_200' ? 'BrainSync Plus' : 'BrainSync Basic';
    const isUnlimited = subscriptionInfo.premium_limit === -1;

    const formatReadingTime = (mins) => {
        if (!mins) return '0 min';
        if (mins < 60) return `${mins} min`;
        const hrs = Math.floor(mins / 60);
        const rem = mins % 60;
        return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
    };

    const statCards = stats ? [
        {
            icon: Video, label: 'Videos transcribed', value: stats.videos_transcribed,
            color: 'text-rose-600 bg-rose-50 border-rose-100',
            caption: stats.videos_transcribed ? 'Reel & lecture audio turned searchable' : null,
        },
        {
            icon: FileText, label: 'Articles summarized', value: stats.articles_summarized,
            color: 'text-sky-600 bg-sky-50 border-sky-100',
            caption: stats.articles_summarized ? 'Long reads compressed to the key points' : null,
        },
        {
            icon: UtensilsCrossed, label: 'Recipes extracted', value: stats.recipes_extracted,
            color: 'text-orange-600 bg-orange-50 border-orange-100',
            caption: stats.recipes_extracted ? 'Full ingredient & step breakdowns from video' : null,
        },
        {
            icon: ImageIcon, label: 'Images analyzed', value: stats.images_analyzed,
            color: 'text-violet-600 bg-violet-50 border-violet-100',
            caption: stats.images_analyzed ? 'OCR + product detection on photos' : null,
        },
        {
            icon: Mic, label: 'Voice notes transcribed', value: stats.audios_transcribed,
            color: 'text-emerald-600 bg-emerald-50 border-emerald-100',
            caption: stats.audios_transcribed ? 'Spoken thoughts turned into text' : null,
        },
        {
            icon: Tag, label: 'Smart tags applied', value: stats.tags_applied,
            color: 'text-indigo-600 bg-indigo-50 border-indigo-100',
            caption: stats.tags_applied ? 'AI-generated tags across your library' : null,
        },
    ].filter(c => c.value > 0) : [];

    return (
        <div
            className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
            data-testid="ai-usage-stats-modal"
            onClick={onClose}
        >
            <div
                className="relative w-full sm:max-w-2xl bg-background rounded-t-3xl sm:rounded-3xl border border-border shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-secondary transition-colors z-10"
                    data-testid="ai-usage-stats-close"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Header / hero value prop */}
                <div className="px-6 pt-7 pb-5 bg-gradient-to-br from-emerald-50 via-white to-amber-50/50 border-b border-border">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Sparkles className="w-4 h-4 text-emerald-600" />
                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                            What your AI has done
                        </span>
                    </div>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground" style={{ lineHeight: '1.2' }}>
                        Your second brain, in numbers
                    </h2>
                    {!loading && stats && stats.minutes_saved > 0 && (
                        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-white border border-emerald-100 p-4 shadow-sm" data-testid="ai-usage-hero-stat">
                            <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                                <Clock className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-2xl font-bold tracking-tight text-foreground leading-none">
                                    ~{formatReadingTime(stats.minutes_saved)}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    of reading & watching saved by AI summaries across{' '}
                                    <span className="font-semibold text-foreground">{stats.total_premium_bookmarks}</span>{' '}
                                    bookmarks on your {tierLabel} plan
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Stat grid */}
                <div className="px-6 py-6">
                    {loading && (
                        <div className="flex items-center justify-center py-10 text-muted-foreground" data-testid="ai-usage-stats-loading">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Tallying your AI activity…
                        </div>
                    )}
                    {error && !loading && (
                        <p className="text-sm text-red-500 text-center py-6" data-testid="ai-usage-stats-error">{error}</p>
                    )}
                    {!loading && !error && stats && statCards.length === 0 && (
                        <div className="text-center py-10" data-testid="ai-usage-stats-empty">
                            <p className="text-sm text-muted-foreground mb-1">No AI processing yet.</p>
                            <p className="text-xs text-muted-foreground/70">
                                Drop a link in the Add modal - we'll transcribe, summarize, and categorize it automatically.
                            </p>
                        </div>
                    )}
                    {!loading && !error && statCards.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {statCards.map(({ icon: Icon, label, value, color, caption }) => (
                                <div
                                    key={label}
                                    className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4"
                                    data-testid={`ai-usage-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center border shrink-0 ${color}`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xl font-bold text-foreground leading-none">{value.toLocaleString()}</p>
                                        <p className="text-xs text-muted-foreground mt-1 font-medium">{label}</p>
                                        {caption && (
                                            <p className="text-[11px] text-muted-foreground/70 leading-snug mt-1">{caption}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Upgrade footer - only shown when user isn't already unlimited */}
                {!isUnlimited && (
                    <div className="px-6 pb-6 pt-2 border-t border-border bg-secondary/30">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-5">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                                    <Crown className="w-4 h-4 text-amber-500" />
                                    Want AI on every single save?
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Go BrainSync Pro and never worry about the quota again.
                                </p>
                            </div>
                            <Button
                                onClick={() => { onClose?.(); onUpgrade?.(); }}
                                className="rounded-xl bg-primary text-white hover:bg-primary/90 shrink-0"
                                data-testid="ai-usage-upgrade-btn"
                            >
                                Upgrade <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
